import { store as getStore } from '../store/store';
import { actions as authActions } from '../store/auth';
import globalSelectors from '../store/global/selectors';
import userSelectors from '../store/user/selectors';

import authCookiesManager from './authCookiesManager';
import { application as OAUTH2 } from './appData';

export const getDefaultHeaders = () => ({ Accept: 'application/json' });

const getUrlWithLanguageData = (uri, lang) => {
    const url = new URL(uri, window.location.origin);
    if (!url.searchParams.has(OAUTH2.languageKey)) {
        url.searchParams.set(OAUTH2.languageKey, lang);
    }
    return url.toString();
};

const _fetch = async (uri, params) => {
    const store = getStore();

    const lang = globalSelectors.getLanguage(store.getState());
    const url = getUrlWithLanguageData(uri, lang);

    const result = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        ...params,
        headers: new Headers({ ...params.headers }),
    });

    if (userSelectors.getUserId(store.getState()) && authCookiesManager.isAnonymous()) {
        store.dispatch(authActions.checkUserToken());
    }

    return result;
};

const HTTPClient = () => ({
    getNoCache: async (uri, headers = {}, skipDefaultHeaders) => {
        const noCache = {
            Cache: 'no-store',
            Pragma: 'no-cache',
            'Cache-Control': 'no-cache',
            Expires: '-1',
        };
        return await _fetch(
            uri,
            {
                method: 'GET',
                headers: {
                    ...(skipDefaultHeaders ? {} : getDefaultHeaders()),
                    ...noCache,
                    ...headers,
                },
            },
            skipDefaultHeaders,
        );
    },
    get: async (uri, headers = {}, skipDefaultHeaders) => {
        return await _fetch(uri, {
            method: 'GET',
            headers: skipDefaultHeaders ? { ...headers } : { ...getDefaultHeaders(), ...headers },
        });
    },
    delete: async (uri, headers = {}) => {
        return await _fetch(uri, {
            method: 'DELETE',
            headers: { ...getDefaultHeaders(), ...headers },
        });
    },
    deleteJson: async (uri, body, headers = {}) => {
        return await _fetch(uri, {
            method: 'DELETE',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json;charset=UTF-8',
                ...headers,
            },
            body: JSON.stringify(body),
        });
    },
    post: async (uri, body, headers = {}) => {
        return await _fetch(uri, {
            method: 'POST',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                ...headers,
            },
            body,
        });
    },
    postJson: async (uri, body, headers = {}) => {
        return await _fetch(uri, {
            method: 'POST',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json;charset=UTF-8',
                ...headers,
            },
            body: body === undefined ? '' : JSON.stringify(body),
        });
    },
    postFormData: async (uri, body, headers = {}) => {
        return await _fetch(uri, {
            method: 'POST',
            headers: {
                ...getDefaultHeaders(),
                ...headers,
            },
            body,
        });
    },
    put: async (uri, body, headers = getDefaultHeaders()) => {
        return await _fetch(uri, {
            method: 'PUT',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                ...headers,
            },
            body,
        });
    },
    putJson: async (uri, body, headers = {}) => {
        return await _fetch(uri, {
            method: 'PUT',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json;charset=UTF-8',
                ...headers,
            },
            body: body === undefined ? '' : JSON.stringify(body),
        });
    },
    patchJson: async (uri, body, headers = getDefaultHeaders()) => {
        return await _fetch(uri, {
            method: 'PATCH',
            headers: {
                ...getDefaultHeaders(),
                'Content-Type': 'application/json',
                ...headers,
            },
            body: body === undefined ? '' : JSON.stringify(body),
        });
    },
    head: async (uri, headers = getDefaultHeaders(), skipDefaultHeaders) => {
        return await _fetch(uri, {
            method: 'HEAD',
            headers: skipDefaultHeaders ? { ...headers } : { ...getDefaultHeaders(), ...headers },
        });
    },
});

export default HTTPClient;
